/*
 * Déconnexion automatique après une période d'inactivité.
 * - L'activité est partagée entre les onglets du site (localStorage) : un onglet laissé en arrière-plan
 *   ne déconnecte pas l'utilisateur tant qu'il travaille dans un autre onglet (CRM, tableau de bord…).
 * - L'avertissement « Rester connecté ? » n'est affiché que dans un onglet visible : un onglet en
 *   arrière-plan ne peut pas l'afficher et le navigateur le refuse aussitôt, ce qui déconnectait le compte.
 * - La déconnexion ne concerne que ce navigateur : les sessions ouvertes sur d'autres appareils restent actives.
 */
import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface UseAutoLogoutProps {
  onLogout: () => void;
  timeoutMinutes?: number;
  isLoggedIn: boolean;
}

/** Dernière activité, tous onglets confondus (horodatage en ms). */
const ACTIVITY_KEY = 'siu:last-activity';
const CHECK_EVERY_MS = 15_000;
/** Écriture partagée au plus toutes les 5 s (mousemove très fréquent). */
const SHARE_EVERY_MS = 5_000;

const readShared = (): number => {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
};
const writeShared = (t: number) => {
  try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch { /* stockage indisponible : activité de cet onglet seulement */ }
};

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'focus'] as const;

export function useAutoLogout({
  onLogout,
  timeoutMinutes = 30,
  isLoggedIn,
}: UseAutoLogoutProps) {
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  // Avertissement 5 minutes avant la déconnexion (à mi-parcours pour un délai très court).
  const WARNING_MS = Math.max(TIMEOUT_MS - 5 * 60 * 1000, TIMEOUT_MS / 2);

  const lastActivityRef = useRef<number>(Date.now());
  const lastShareRef = useRef<number>(0);
  const loggingOutRef = useRef(false);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const lastActivity = useCallback(() => Math.max(lastActivityRef.current, readShared()), []);

  const recordActivity = useCallback((force = false) => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (force || now - lastShareRef.current >= SHARE_EVERY_MS) {
      lastShareRef.current = now;
      writeShared(now);
    }
  }, []);

  const logout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      // Session de ce navigateur uniquement (tous ses onglets) ; les autres appareils restent connectés.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Error during auto-logout:', error);
    }
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('supabase.auth.') || key.startsWith('sb-')) localStorage.removeItem(key);
      });
      sessionStorage.clear();
    } catch { /* stockage indisponible */ }
    alert(`Déconnexion automatique\n\nVous avez été déconnecté après ${timeoutMinutes} minutes d'inactivité pour protéger votre compte.`);
    onLogoutRef.current();
  }, [timeoutMinutes]);

  /** Contrôle périodique : déconnexion après le délai, avertissement avant (onglet visible seulement). */
  const check = useCallback(() => {
    if (!isLoggedIn || loggingOutRef.current) return;
    const idle = Date.now() - lastActivity();
    if (idle >= TIMEOUT_MS) { void logout(); return; }
    if (idle < WARNING_MS || document.hidden) return;
    const askedAt = Date.now();
    const stay = window.confirm(
      'Session d\'inactivité détectée\n\n' +
      'Vous serez automatiquement déconnecté dans quelques minutes pour des raisons de sécurité.\n\n' +
      'Cliquez sur "OK" pour rester connecté ou "Annuler" pour vous déconnecter maintenant.'
    );
    if (stay) { recordActivity(true); return; }
    // Fenêtre refusée par le navigateur sans être affichée (onglet passé en arrière-plan) : pas de déconnexion.
    if (document.hidden || Date.now() - askedAt < 300) return;
    void logout();
  }, [isLoggedIn, TIMEOUT_MS, WARNING_MS, lastActivity, logout, recordActivity]);

  useEffect(() => {
    if (!isLoggedIn) return;
    loggingOutRef.current = false;
    recordActivity(true);

    const onActivity = () => recordActivity();
    // Retour sur l'onglet : délai dépassé dans tous les onglets → déconnexion, sinon activité.
    const onVisible = () => {
      if (document.hidden) return;
      check();
      if (!loggingOutRef.current) recordActivity();
    };

    ACTIVITY_EVENTS.forEach(event => document.addEventListener(event, onActivity, true));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const interval = window.setInterval(check, CHECK_EVERY_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(event => document.removeEventListener(event, onActivity, true));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.clearInterval(interval);
    };
  }, [isLoggedIn, check, recordActivity]);

  const resetTimer = useCallback(() => {
    if (isLoggedIn) recordActivity(true);
  }, [isLoggedIn, recordActivity]);

  const getTimeRemaining = useCallback(() => {
    if (!isLoggedIn) return 0;
    return Math.max(0, TIMEOUT_MS - (Date.now() - lastActivity()));
  }, [isLoggedIn, TIMEOUT_MS, lastActivity]);

  return {
    resetTimer,
    getTimeRemaining,
    isActive: isLoggedIn,
  };
}
